import { icon } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { objectiveNeighbors, type TutorialView } from "./tutorial-model.ts";

/**
 * Cartão de objetivos (doc 05 e 06; F81: o do mockup ui-b, em cima à esquerda): o ◆! e o título do passo,
 * três linhas com o círculo de check (o passo anterior feito, a tarefa à vista e o próximo) e os dois
 * botões. Não pega cliques fora dos botões. A tarefa concluída fica riscada 1,5 s; a seta aponta o alvo no
 * mundo e fica presa à borda quando ele sai da vista.
 */
export class TutorialCard {
  readonly element: HTMLElement;
  readonly arrow: HTMLElement;
  private readonly heading: HTMLElement;
  private readonly task: HTMLElement;
  private readonly current: HTMLElement;
  private readonly previous: HTMLElement;
  private readonly next: HTMLElement;
  private readonly skipButton: HTMLButtonElement;
  private key = "";

  constructor(parent: HTMLElement, onSkip: () => void, onDisable: () => void) {
    // O botão perde o foco depois do clique: Enter e Espaço voltam a ser do jogo (chat, pulo).
    const press = (action: () => void) => (e: Event) => {
      (e.currentTarget as HTMLElement | null)?.blur();
      action();
    };
    this.heading = h("h3", {});
    this.task = h("p", { class: "tarefa" });
    this.previous = h("li", { class: "objetivo feito" });
    this.current = h("li", { class: "objetivo atual" }, this.task);
    this.next = h("li", { class: "objetivo proximo" });
    this.skipButton = h("button", { type: "button", id: "tutorial-pular", onclick: press(onSkip) }, "Pular");
    this.element = h(
      "section",
      { id: "tutorial", class: "hud-cartao", role: "status", "aria-label": "Tutorial" },
      h("header", { class: "objetivos-topo" }, icon("hud-alerta"), this.heading),
      h("ul", { class: "objetivos" }, this.previous, this.current, this.next),
      h(
        "div",
        { class: "acoes" },
        this.skipButton,
        h("button", { type: "button", id: "tutorial-desligar", onclick: press(onDisable) }, "Desligar tutorial"),
      ),
    );
    this.arrow = h("div", { id: "tutorial-seta", "aria-hidden": "true" }, "➤");
    this.element.hidden = true;
    this.arrow.hidden = true;
    parent.append(this.element, this.arrow);
  }

  update(view: TutorialView | undefined): void {
    const key = view ? `${view.index}:${view.done}:${view.text}` : "";
    if (key === this.key) return;
    this.key = key;
    this.element.hidden = !view;
    if (!view) return;
    this.heading.textContent = `Tutorial · ${view.index}/${view.total} · ${view.title}`;
    this.task.textContent = view.done ? `✓ ${view.text}` : view.text;
    this.element.classList.toggle("feito", view.done);
    this.current.classList.toggle("feito", view.done);
    const around = objectiveNeighbors(view.index);
    this.previous.textContent = around.previous ?? "";
    this.previous.hidden = !around.previous;
    this.next.textContent = around.next ?? "";
    this.next.hidden = !around.next;
    // Riscada, a tarefa já foi: "Pular" não pode levar a seguinte, que ainda nem apareceu.
    this.skipButton.disabled = view.done;
  }

  /** Seta na tela (x, y), girada para o alvo; `undefined` esconde. */
  point(at: { x: number; y: number; angle: number; off: boolean } | undefined): void {
    this.arrow.hidden = !at;
    if (!at) return;
    // Na vista, a seta fica acima do alvo apontando para baixo; fora dela, presa à borda apontando para ele.
    const angle = at.off ? at.angle : Math.PI / 2;
    this.arrow.style.transform = `translate(${at.x.toFixed(1)}px, ${(at.off ? at.y : at.y - 28).toFixed(1)}px) rotate(${angle.toFixed(3)}rad)`;
  }

  get visible(): boolean {
    return !this.element.hidden;
  }

  get text(): string {
    return this.task.textContent ?? "";
  }

  remove(): void {
    this.element.remove();
    this.arrow.remove();
  }
}
